/*
 * The body diff, unified or split.
 *
 * Split mode is not two independent lists: a removed run and the added run that
 * replaced it have to sit on the same rows, or the reader has to do the alignment
 * themselves and the mode is worse than useless. `alignHunks` pairs each
 * deletion/addition run positionally and pads the shorter side, which is the same
 * rule a side-by-side viewer uses.
 *
 * The two panes are also scroll-locked, for the same reason: unsynced panes make
 * the reader hold a scroll position in their head.
 */
import { useCallback, useRef } from 'react'
import type { DiffHunk, DiffLine } from '../../../api/knowledgeBases.ts'

export type DiffMode = 'unified' | 'split'

interface DiffViewProps {
  hunks: DiffHunk[]
  mode?: DiffMode
  /** Height cap for the scrolling region; omit inside an already-scrolling card. */
  capped?: boolean
}

export function DiffView({ hunks, mode = 'unified', capped = true }: DiffViewProps) {
  if (hunks.length === 0) {
    return (
      <div className="diff">
        <div className="diff__line diff__line--ctx">Изменений в теле нет.</div>
      </div>
    )
  }
  return mode === 'split' ? (
    <SplitDiff hunks={hunks} capped={capped} />
  ) : (
    <UnifiedDiff hunks={hunks} capped={capped} />
  )
}

function UnifiedDiff({ hunks, capped }: { hunks: DiffHunk[]; capped: boolean }) {
  return (
    <div className={`diff${capped ? ' diff--unified' : ''}`}>
      {hunks.map((hunk, hunkIndex) => (
        <div key={`${hunk.header}-${hunkIndex}`}>
          {hunk.header ? <div className="diff__line diff__line--hunk">{hunk.header}</div> : null}
          {(hunk.lines ?? []).map((line, lineIndex) => (
            <div key={lineIndex} className={`diff__line ${lineClass(line.kind)}`}>
              {sigil(line.kind)}
              {line.text}
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

function SplitDiff({ hunks, capped }: { hunks: DiffHunk[]; capped: boolean }) {
  const leftRef = useRef<HTMLDivElement>(null)
  const rightRef = useRef<HTMLDivElement>(null)
  // Guards the echo: setting one pane's scrollTop fires its own scroll event, which
  // would set the first pane back and fight the user's wheel.
  const syncing = useRef(false)

  const sync = useCallback((from: HTMLDivElement | null, to: HTMLDivElement | null) => {
    if (!from || !to || syncing.current) return
    syncing.current = true
    to.scrollTop = from.scrollTop
    to.scrollLeft = from.scrollLeft
    requestAnimationFrame(() => {
      syncing.current = false
    })
  }, [])

  const rows = alignHunks(hunks)

  return (
    <div className="diff-split">
      <div
        ref={leftRef}
        className={`diff${capped ? ' diff-split__pane' : ''}`}
        onScroll={() => sync(leftRef.current, rightRef.current)}
      >
        {rows.map((row, index) => (
          <SplitLine key={index} line={row.left} />
        ))}
      </div>
      <div
        ref={rightRef}
        className={`diff${capped ? ' diff-split__pane' : ''}`}
        onScroll={() => sync(rightRef.current, leftRef.current)}
      >
        {rows.map((row, index) => (
          <SplitLine key={index} line={row.right} />
        ))}
      </div>
    </div>
  )
}

function SplitLine({ line }: { line: DiffLine | null }) {
  // A padded row still occupies a line — that is what keeps the panes aligned.
  if (line === null) {
    return <div className="diff__line diff__line--empty">{' '}</div>
  }
  return (
    <div className={`diff__line ${lineClass(line.kind)}`}>
      {sigil(line.kind)}
      {line.text || ' '}
    </div>
  )
}

export interface AlignedRow {
  left: DiffLine | null
  right: DiffLine | null
}

/**
 * Lay the hunks out as aligned row pairs.
 *
 * Context lines appear on both sides. A run of deletions is paired index-for-index
 * with the run of additions that follows it, and whichever run is shorter is padded
 * with blanks, so a two-for-five rewrite reads as a rewrite rather than as two
 * unrelated lists that happen to be adjacent.
 */
export function alignHunks(hunks: DiffHunk[]): AlignedRow[] {
  const rows: AlignedRow[] = []
  for (const hunk of hunks) {
    if (hunk.header) {
      const header: DiffLine = { kind: 'hunk', text: hunk.header }
      rows.push({ left: header, right: header })
    }
    let index = 0
    const lines = hunk.lines ?? []
    while (index < lines.length) {
      const line = lines[index] as DiffLine
      if (line.kind !== 'del' && line.kind !== 'add') {
        rows.push({ left: line, right: line })
        index += 1
        continue
      }
      const dels: DiffLine[] = []
      const adds: DiffLine[] = []
      while (index < lines.length && lines[index]?.kind === 'del') {
        dels.push(lines[index++] as DiffLine)
      }
      while (index < lines.length && lines[index]?.kind === 'add') {
        adds.push(lines[index++] as DiffLine)
      }
      const height = Math.max(dels.length, adds.length)
      for (let row = 0; row < height; row += 1) {
        rows.push({ left: dels[row] ?? null, right: adds[row] ?? null })
      }
    }
  }
  return rows
}

function lineClass(kind: string): string {
  switch (kind) {
    case 'add':
      return 'diff__line--add'
    case 'del':
      return 'diff__line--del'
    case 'hunk':
      return 'diff__line--hunk'
    default:
      return 'diff__line--ctx'
  }
}

function sigil(kind: string): string {
  if (kind === 'add') return '+ '
  if (kind === 'del') return '− '
  if (kind === 'hunk') return ''
  return '  '
}
